import React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import { Box, Typography, Tooltip } from "@mui/material";
import { Loop } from "@mui/icons-material";
import { useTasks } from "../../hooks/useTasks.js";

/** ---------- Theme (match your existing) ---------- */
const bg = "rgb(248, 248, 248)";
const muted = "rgba(0,0,0,0.45)";
const text = "rgba(0,0,0,0.85)";
const border = "1px solid rgba(0,0,0,0.06)";

/** ---------- Local Date Helpers ---------- */
function ymdLocal(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// DST-safe: always anchor calculations at midday local time
function atLocalNoon(d) {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return x;
}

// Helper to get consistent date key from task
const keyFromTask = (t) => t?.dateString || (t?.date ? ymdLocal(t.date) : null);

/** ---------- Helpers ---------- */
function toMinutes(timeStr) {
  if (!timeStr) return NaN;
  const s = String(timeStr).trim().toUpperCase();

  // Matches:
  // "05:45", "5:45", "05:45 AM", "5:45PM", "17:30"
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/);
  if (!m) return NaN;

  let h = Number(m[1]);
  const min = Number(m[2] ?? "0");
  const mer = m[3]; // AM/PM or undefined

  if (Number.isNaN(h) || Number.isNaN(min)) return NaN;

  if (mer) {
    // 12h -> 24h
    if (h === 12) h = 0;
    if (mer === "PM") h += 12;
  }

  return h * 60 + min;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function formatHM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${hh}:${mm}`;
}

function formatDuration(mins) {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function formatDayHeader(date) {
  const dow = date.toLocaleString(undefined, { weekday: "short" });
  const dayNum = date.getDate();
  return { dow, dayNum };
}

function yFromMinute(minute, startMin, pxPerMin) {
  return (minute - startMin) * pxPerMin;
}

/** ---------- Minimal icon (no text leakage) ---------- */
function MiniGlyph({ kind = "clock" }) {
  // simple, fast inline SVGs (no dependency on material symbols font)
  if (kind === "sun") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="white"
          d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.8 1.42-1.42zM1 13h3v-2H1v2zm10-9h2V1h-2v3zm9 7v2h3v-2h-3zm-1.34-6.54l1.79-1.8-1.41-1.41-1.8 1.79 1.42 1.42zM17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0zm-6 11h2v-3h-2v3zm7.24-3.84 1.8 1.79 1.41-1.41-1.79-1.8-1.42 1.42zM4.46 19.78l1.41 1.41 1.8-1.79-1.42-1.42-1.79 1.8z"
        />
      </svg>
    );
  }
  // default clock
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="white"
        d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 .001-16.001A8 8 0 0 1 12 20Zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7Z"
      />
    </svg>
  );
}

/** ---------- Time gutter (shared across week) ---------- */
const TimeGutter = React.memo(function TimeGutter({ startMin, endMin, pxPerMin }) {
  const height = (endMin - startMin) * pxPerMin;

  // label every hour (clean week view)
  const marks = [];
  const firstHour = Math.ceil(startMin / 60);
  const lastHour = Math.floor(endMin / 60);
  for (let h = firstHour; h <= lastHour; h++) marks.push(h * 60);

  return (
    <Box sx={{ position: "relative", height, width: 72 }}>
      {marks.map((m) => {
        const y = yFromMinute(m, startMin, pxPerMin);
        return (
          <Typography
            key={m}
            sx={{
              position: "absolute",
              top: y - 8,
              left: 10,
              fontSize: 12,
              color: muted,
              fontWeight: 600,
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            {formatHM(m)}
          </Typography>
        );
      })}
    </Box>
  );
});

/** ---------- Task node (no details unless hover) ---------- */
const TaskNode = React.memo(function TaskNode({
  task,
  startMin,
  pxPerMin,
  minGapPx = 6,
  onTaskClick,
}) {
  const s = task.startTime ? toMinutes(task.startTime) : startMin;
  const e = task.endTime ? toMinutes(task.endTime) : s + 30;
  const dur = Math.max(1, e - s);

  const top = Number.isFinite(task._top) ? task._top : yFromMinute(s, startMin, pxPerMin);
  const heightRaw = dur * pxPerMin;
  const height = clamp(heightRaw, 28, 140);

  const isCircle = task._shape === "circle";
  const w = 36;
  const r = isCircle ? 999 : 18;

  const palette = ["#F49F99", "#6366F1", "#10B981", "#F59E0B", "#A855F7", "#14B8A6"];
  const idx = task.id ? task.id.charCodeAt(task.id.length - 1) % palette.length : 0;
  const fill = task.color || palette[idx];

  const label = `${task.startTime ?? ""}${task.endTime ? ` – ${task.endTime}` : ""} (${formatDuration(
    Math.max(0, e - s)
  )})`;
  const title = task.title || "Task";

  return (
    <Tooltip
      arrow
      placement="right"
      enterDelay={120}
      title={
        <Box sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{label}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "white" }}>{title}</Typography>
            {task.isRecurring && (
              <Loop
                sx={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.75)",
                }}
                title="Recurring"
              />
            )}
          </Box>
        </Box>
      }
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onTaskClick?.(task)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onTaskClick?.(task);
        }}
        sx={{
          position: "absolute",
          top,
          left: 0,
          width: "100%",
          height: Math.max(height, 28),
          cursor: "pointer",
          outline: "none",
          "&:hover .laneHover": { opacity: 1 },
          "&:hover .node": { transform: "translate(-50%, -1px)", boxShadow: "0 14px 28px rgba(0,0,0,0.14)" },
        }}
      >
        {/* subtle lane highlight behind (centered, not left-biased) */}
        <Box
          className="laneHover"
          sx={{
            pointerEvents: "none",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: "78%",
            height: Math.max(height, 40),
            borderRadius: 3,
            bgcolor: "rgba(244,159,153,0.07)",
            opacity: 0,
            transition: "opacity 160ms ease",
          }}
        />

        {/* marker centered on the day spine */}
        <Box
          className="node"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: w,
            height: isCircle ? w : Math.max(height, 44),
            borderRadius: r,
            bgcolor: fill,
            display: "grid",
            placeItems: "center",
            boxShadow: "0 10px 22px rgba(0,0,0,0.12)",
            transition: "transform 160ms ease, box-shadow 160ms ease",
          }}
        >
          <MiniGlyph kind={"clock"} />
        </Box>
      </Box>
    </Tooltip>
  );
});

/** ---------- Day column with centered spine + tasks ---------- */
const DayColumn = React.memo(function DayColumn({
  tasks,
  startMin,
  endMin,
  pxPerMin,
  onTaskClick,
}) {
  const { tokens } = useAppTheme();
  const height = (endMin - startMin) * pxPerMin;

  const sorted = React.useMemo(() => {
    const t = (tasks || []).filter((x) => x && !Number.isNaN(toMinutes(x.startTime)));
    t.sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));

    // Shape rule: first/last circle, others pill
    if (t.length > 0) {
      t[0] = { ...t[0], _shape: "circle" };
      if (t.length > 1) t[t.length - 1] = { ...t[t.length - 1], _shape: "circle" };
      for (let i = 1; i < t.length - 1; i++) t[i] = { ...t[i], _shape: "pill" };
    }
    return t;
  }, [tasks]);

  // Collision avoidance to prevent overlapping tasks
  const laidOut = React.useMemo(() => {
    const minGapPx = 8;
    const minNodeH = 44;

    let cursor = -Infinity;

    return sorted.map((task) => {
      const s = toMinutes(task.startTime);
      const e = task.endTime ? toMinutes(task.endTime) : s + 30;
      const dur = Math.max(1, e - s);

      const idealTop = (s - startMin) * pxPerMin;
      const idealH = clamp(dur * pxPerMin, 28, 140);
      const nodeH = task._shape === "circle" ? 36 : Math.max(idealH, minNodeH);

      const top = Math.max(idealTop, cursor + minGapPx);
      cursor = top + nodeH;

      return { ...task, _top: top };
    });
  }, [sorted, startMin, pxPerMin]);

  return (
    <Box sx={{ position: "relative", height, width: "100%" }}>
      {/* Centered spine (no "column card" look) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 2,
          bgcolor: tokens.primaryHover,
          borderRadius: 2,
          opacity: 0.85,
        }}
      />

      {laidOut.map((task) => (
        <TaskNode
          key={task.id}
          task={task}
          startMin={startMin}
          pxPerMin={pxPerMin}
          onTaskClick={onTaskClick}
        />
      ))}
    </Box>
  );
});

/** ---------- WeekView (drop-in) ---------- */
export function WeekView({
  user,
  weekStart,
  spriteUrl, // kept for compatibility (not used in this simplified week view)
  onTaskClick,
  onTasksLoaded,
}) {
  const { tokens } = useAppTheme();
  /** week dates: Sunday..Saturday */
  const weekDates = React.useMemo(() => {
    const dates = [];

    const start = atLocalNoon(weekStart);        // <— important
    const dayOfWeek = start.getDay();            // 0=Sun..6=Sat

    const sunday = new Date(start);
    sunday.setDate(start.getDate() - dayOfWeek); // move back to Sunday
    sunday.setHours(12, 0, 0, 0);                // keep it anchored

    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      d.setHours(12, 0, 0, 0);
      dates.push(d);
    }

    return dates;
  }, [weekStart]);

  /** Use useTasks hook to get real tasks for the current week */
  const { tasks: allTasks, loading, error } = useTasks(user, null, { onAddTask: undefined });

  /** Filter tasks to only include tasks in the current week */
  const weekTasks = React.useMemo(() => {
    if (!allTasks || allTasks.length === 0) return [];
    
    // Get the week date range (Sunday to Saturday)
    const weekStart = new Date(weekDates[0]);
    const weekEnd = new Date(weekDates[6]);
    weekEnd.setHours(23, 59, 59, 999); // End of Saturday
    
    return allTasks.filter(task => {
      const taskDate = task.dateString || (task.date ? task.date.toISOString().split('T')[0] : null);
      if (!taskDate) return false;
      
      const taskDateTime = new Date(taskDate);
      return taskDateTime >= weekStart && taskDateTime <= weekEnd;
    });
  }, [allTasks, weekDates]);

  /** Group tasks by date for week view */
  const tasksByDate = React.useMemo(() => {
    // Group tasks by date using consistent keyFromTask
    const map = {};
    
    if (!weekTasks || weekTasks.length === 0) {
      return map;
    }
    
    for (const task of weekTasks) {
      const k = keyFromTask(task);
      if (!k) continue;
      (map[k] ||= []).push(task);
    }
    
    return map;
  }, [weekTasks]);

  /** Update onTasksLoaded when tasks change */
  React.useEffect(() => {
    const totalTasks = Object.values(tasksByDate).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    onTasksLoaded?.(totalTasks);
  }, [tasksByDate, onTasksLoaded]);

  /** Compute global time range across whole week so all days align */
  const { startMin, endMin } = React.useMemo(() => {
    const all = Object.values(tasksByDate).flat();
    const times = all
      .filter((t) => t?.startTime)
      .map((t) => toMinutes(t.startTime))
      .concat(
        all.filter((t) => t?.endTime).map((t) => toMinutes(t.endTime))
      );

    // default range if empty
    if (!times.length) return { startMin: 6 * 60, endMin: 19 * 60 };

    const min = Math.min(...times);
    const max = Math.max(...times);

    // pad to nearest hour for cleanliness
    const padTop = Math.floor(min / 60) * 60;
    const padBot = Math.ceil(max / 60) * 60;

    return { startMin: padTop, endMin: Math.max(padBot, padTop + 60) };
  }, [tasksByDate]);

  /** pxPerMin: choose once; you can tune */
  const pxPerMin = 2.1; // ~clean density; increase for more "air"
  const gridHeight = (endMin - startMin) * pxPerMin;

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <Typography sx={{ color: "text.secondary" }}>Loading week…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <Typography sx={{ color: "error.main" }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", background: bg }}>
      {/* Horizontal scroll safety */}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 900 }}>
          {/* Sticky day header row */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              background: "rgba(248,248,248,0.86)",
              backdropFilter: "blur(10px)",
              borderBottom: border,
              px: 2,
              py: 1.25,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "72px repeat(7, 1fr)",
                columnGap: 0, // Match task columns for alignment
                alignItems: "center",
              }}
            >
              <Box />
              {weekDates.map((d) => {
                const { dow, dayNum } = formatDayHeader(d);
                const isToday = new Date().toDateString() === d.toDateString();
                const isSelected = false; // keep compatibility if you later add selection
                return (
                  <Box
                    key={d.toISOString()}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "rgba(0,0,0,0.50)" }}>
                      {dow}
                    </Typography>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 800,
                        fontSize: 12,
                        color: isToday ? "white" : text,
                        background: isToday ? tokens.primary : "rgba(0,0,0,0.05)",
                        border: isToday ? `2px solid ${tokens.primary}` : "1px solid rgba(0,0,0,0.1)",
                        boxShadow: isToday ? `0 0 0 2px ${tokens.primarySubtle}` : "none",
                      }}
                    >
                      {dayNum}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Main content: one shared vertical scroll, aligned by time */}
          <Box
            sx={{
              height: "calc(100vh - 220px)", // adjust based on your header height
              overflowY: "auto",
              px: 2,
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "72px repeat(7, 1fr)",
                columnGap: 0, // Remove gap for true single canvas
                alignItems: "start",
              }}
            >
              {/* Left gutter */}
              <TimeGutter startMin={startMin} endMin={endMin} pxPerMin={pxPerMin} />

              {/* 7 day columns */}
              {weekDates.map((date, idx) => {
                const key = ymdLocal(date);
                const dayTasks = tasksByDate[key] || [];

                return (
                  <DayColumn
                    key={key}
                    tasks={dayTasks}
                    startMin={startMin}
                    endMin={endMin}
                    pxPerMin={pxPerMin}
                    onTaskClick={onTaskClick}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default WeekView;